import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { TextButton } from 'components/v2/buttons';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { LabelGroup } from 'components/v2/form/styled';
import { ExpandableHelpBox } from 'components/v2/help-boxes';
import { FieldState, MaterialPropertyItemModel, MaterialPropertyModel } from 'services/EpdClient';
import {
  DoubleFieldGrid,
  ExpandablePanelHeaderName,
  FieldContainer,
  FieldPanelInput,
  HelpBoldText,
} from 'styles/v2/Styles.styled';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import { ErrorAlertPanel } from 'components/v2/compiler/panels/Styles.styled';
import ToggleButton from 'components/v2/compiler/panels/toggleable/ToggleButton';
import { StatusAttentionIcon } from 'components/v2/icons';

type Props = {
  materialProperties: MaterialPropertyModel[];
  epdMaterialProperties: MaterialPropertyItemModel[] | undefined;
  epdMaterialPropertiesState: FieldState | undefined;
  onSave(newItems: MaterialPropertyItemModel[]): Promise<void>;
  isConstruction?: boolean;
};

const MaterialPropertiesCopySection: React.FC<Props> = ({
  materialProperties,
  epdMaterialProperties,
  epdMaterialPropertiesState,
  onSave,
  isConstruction,
}) => {
  // Unlike compiler, material properties of the old wizard & copy function don't allow empty values in either of the fields.
  // That's why we can't constantly sync data with server, otherwise items which are being edited, will be overwritten.
  // Anyway, the incomplete items will be lost when the page is refreshed...
  const [ephemeralRows, setEphemeralRows] = useState<MaterialPropertyItemModel[] | undefined>(epdMaterialProperties);
  const { t } = useTranslation();
  const saveMutation = useMutation({
    mutationFn: onSave,
    scope: { id: '1' }, // Just to prevent mutations from runnig concurrently
  });

  useEffect(() => {
    if (!ephemeralRows && epdMaterialProperties) {
      setEphemeralRows(epdMaterialProperties);
    }
  }, [epdMaterialProperties]);

  const handleChangeProperty = (ephemeralIndex: number, propertyName: string, newValue: any) => {
    if (!ephemeralRows) {
      return undefined;
    }

    const newRows = ephemeralRows.map((old, i) => (i === ephemeralIndex ? { ...old, [propertyName]: newValue } : old));
    setEphemeralRows(newRows);
    return newRows;
  };

  const handleCreateRow = () => {
    if (!ephemeralRows) {
      return;
    }

    const newRows = [...ephemeralRows, { materialPropertyId: undefined, value: undefined }];
    setEphemeralRows(newRows);
  };

  const handleDeleteRow = (ephemeralIndex: number) => {
    if (!ephemeralRows) {
      return;
    }

    const newRows = ephemeralRows.filter((_, i) => i !== ephemeralIndex);
    setEphemeralRows(newRows);
    handleSave(newRows);
  };

  const handleSave = (newData: MaterialPropertyItemModel[]) => {
    const completeRows = newData.filter((d) => typeof d.materialPropertyId === 'string' && typeof d.value === 'number');
    saveMutation.mutate(completeRows);
  };

  const headerHelpBoxTemplate = (options: any, header: string) => {
    return (
      <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
        <ExpandablePanelHeaderName>
          <HelpBoldText>{header}</HelpBoldText>
        </ExpandablePanelHeaderName>
        <ToggleButton options={options} withMargins />
      </div>
    );
  };

  const checkIfSectionHasErrors = () => {
    return (
      (!!isConstruction && ephemeralRows?.some((mp) => !mp.materialPropertyId || !mp.value)) ||
      (epdMaterialPropertiesState?.errorMessage &&
        !epdMaterialPropertiesState?.errorMessage.startsWith('Material properties are required for construction'))
    );
  };

  return (
    <>
      <FieldContainer>
        <FieldPanelInput>
          <LabelGroup>
            <span style={{ fontWeight: 'bold' }}>MATERIAL PROPERTIES</span>
            <TooltipHelpIcon content={t('epdWizard.wizardStep2.tooltips.materialProperties')} position={'right'} />
            {checkIfSectionHasErrors() && <StatusAttentionIcon />}
          </LabelGroup>
          <ExpandableHelpBox
            headerTemplate={(options) => headerHelpBoxTemplate(options, 'What are material properties?')}
            toggleable
          >
            <span>
              Declaration of relevant non-scaling physical or chemical product properties.
              <br />
              <br />
              Click on the '+Add property' button to add a new property, then select a unit and provide a value. To remove a
              property, click the delete icon.
            </span>
          </ExpandableHelpBox>
        </FieldPanelInput>
      </FieldContainer>
      <DoubleFieldGrid style={{ marginTop: 22 }}>
        {ephemeralRows?.map((item, index) => (
          <React.Fragment key={index}>
            <WizardSelectControlledComponent
              options={(materialProperties || [])
                .filter((x) => {
                  if (
                    x.fullName?.startsWith('Conversion factor to mass') ||
                    x.fullName?.startsWith('Grammage (kg') ||
                    x.fullName?.startsWith('Gross density (kg')
                  ) {
                    return false;
                  }

                  return true;
                })
                .map((x: MaterialPropertyModel) => {
                  return {
                    value: x.id,
                    label: x.fullName,
                    isDisabled: ephemeralRows.some((mp) => mp.materialPropertyId === x.id),
                  };
                })
                .filter((option) => !ephemeralRows?.find((rowValue: any) => rowValue.id === option.value))}
              value={
                materialProperties
                  .filter((p) => p.id === item.materialPropertyId)
                  .map((x) => ({ value: x.id, label: x.fullName }))[0]
              }
              label="Property(unit)"
              required={epdMaterialPropertiesState?.mandatory}
              tooltip="Select unit and provide a value."
              isClearable={true}
              name="materialPropertyId"
              onChanged={(propertyName: string, value: any) => {
                const newData = handleChangeProperty(index, propertyName, value);
                if (newData) {
                  handleSave(newData);
                }
              }}
              {...reactSelectPropsForHtmlLabel}
            />
            <div className="flex">
              <WizardTextInputControlledComponent
                value={item.value}
                type="number"
                label="value"
                required={epdMaterialPropertiesState?.mandatory}
                tooltip="Numeric field"
                placeholder={t('placeholder.insertNumber')}
                name="value"
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const parsedValue =
                    inputValue === undefined || inputValue === null || inputValue === '' ? null : Number(inputValue);
                  handleChangeProperty(index, 'value', parsedValue);
                }}
                onChanged={() => handleSave(ephemeralRows)}
              />

              <TextButton
                style={{ alignSelf: 'flex-end', margin: '0.25rem' }}
                type="button"
                text
                icon="pi pi-trash"
                onClick={() => handleDeleteRow(index)}
              />
            </div>
          </React.Fragment>
        ))}
      </DoubleFieldGrid>
      {ephemeralRows && ephemeralRows.length < 6 && (
        <TextButton
          className="white-space-nowrap"
          style={{ cursor: 'pointer', paddingTop: '3px', paddingBottom: 0 }}
          label={'+ Add property'}
          text
          onClick={handleCreateRow}
        />
      )}
      {epdMaterialPropertiesState?.errorMessage &&
        !epdMaterialPropertiesState?.errorMessage.startsWith('Material properties are required for construction') && (
          <ErrorAlertPanel style={{ marginTop: '30px' }} className="w-full">
            {epdMaterialPropertiesState.errorMessage}
          </ErrorAlertPanel>
        )}
    </>
  );
};

export default MaterialPropertiesCopySection;
